import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Paperclip, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;
  conversationId?: string;
  onFileAnalyzed?: (file: File, conversationId?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function MessageInput({
  value,
  onChange,
  onSend,
  isLoading,
  conversationId,
  onFileAnalyzed,
  disabled,
  placeholder = "Skriv din melding her...",
  className
}: MessageInputProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading && value.trim() && !disabled) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onFileAnalyzed) {
      setIsUploading(true);
      try {
        await onFileAnalyzed(file, conversationId);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && onFileAnalyzed) {
      setIsUploading(true);
      try {
        await onFileAnalyzed(file, conversationId);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div
      className={cn(
        "flex gap-2 items-center p-2 rounded-lg transition-colors",
        isDragging && "bg-orange-50 border-2 border-dashed border-orange-300",
        isMobile && "flex-col",
        className
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={isLoading || isUploading || disabled}
        className={cn(
          "flex-1",
          "min-h-[44px]",
          isMobile && "text-base w-full mb-2 min-h-[50px]",
          "!md:text-sm",
          "px-4 py-2"
        )}
      />
      <div className={cn("flex gap-2", isMobile && "w-full justify-end")}>
        <Button
          variant="outline"
          size={isMobile ? "default" : "icon"}
          type="button"
          disabled={isLoading || isUploading || disabled}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "hover:bg-orange-50",
            "min-h-[44px]",
            "touch-manipulation"
          )}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Paperclip className="h-4 w-4" />
              {isMobile && <span className="ml-2">Last opp</span>}
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt"
        />
        <Button 
          onClick={onSend}
          disabled={isLoading || isUploading || disabled || !value.trim()}
          className={cn(
            "bg-orange-500 hover:bg-orange-600",
            "min-h-[44px]",
            "touch-manipulation"
          )}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4" />
              {isMobile && <span className="ml-2">Send</span>}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}